import { useState, useEffect, useCallback } from "react";

import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Check, ExternalLink } from "lucide-react";
import toast from "react-hot-toast";

import { cn } from "@/lib/utils";
import { DynamicIcon } from "@/components/skills-marketplace/dynamic-icon";
import { MarkdownViewer } from "@/components/skills-marketplace/markdown-viewer";
import { skillsMarketplaceApi } from "@/lib/api/skills-marketplace";
import type { MarketplaceSkillFull } from "@optidev/shared-types";

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function SkillDetail() {
  const { skillId } = useParams<{ skillId: string }>();
  const navigate = useNavigate();

  const [skill, setSkill] = useState<MarketplaceSkillFull | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallLoading, setIsInstallLoading] = useState(false);

  useEffect(() => {
    if (!skillId) return;
    setIsLoading(true);
    skillsMarketplaceApi
      .getSkillDetail(skillId)
      .then(setSkill)
      .catch((err) => {
        console.error("Failed to fetch skill:", err);
        toast.error("Skill not found");
        navigate("/dashboard/skills");
      })
      .finally(() => setIsLoading(false));
  }, [skillId, navigate]);

  useEffect(() => {
    if (!skillId) return;
    skillsMarketplaceApi
      .getInstalled()
      .then((installed) => {
        setIsInstalled(installed.some((s) => s.id === skillId));
      })
      .catch(() => {});
  }, [skillId]);

  const handleInstall = useCallback(async () => {
    if (!skillId) return;
    setIsInstallLoading(true);
    try {
      await skillsMarketplaceApi.install(skillId);
      setIsInstalled(true);
      toast.success("Skill installed");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to install";
      toast.error(message);
    } finally {
      setIsInstallLoading(false);
    }
  }, [skillId]);

  const handleUninstall = useCallback(async () => {
    if (!skillId) return;
    setIsInstallLoading(true);
    try {
      await skillsMarketplaceApi.uninstall(skillId);
      setIsInstalled(false);
      toast.success("Skill uninstalled");
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to uninstall";
      toast.error(message);
    } finally {
      setIsInstallLoading(false);
    }
  }, [skillId]);

  if (isLoading || !skill) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10 overflow-x-hidden">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10 overflow-x-hidden">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
          <button
            onClick={() => navigate("/dashboard/skills")}
            className="hover:text-foreground transition-colors"
          >
            Skills Marketplace
          </button>
          <span>/</span>
          <span className="text-foreground/70 truncate">{skill.name}</span>
        </div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">{skill.name}</h1>
      </div>

      {/* Two-column layout */}
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="rounded-lg border border-border/50 bg-card p-5 sm:p-8 min-h-[calc(100vh-160px)] flex flex-col">
            <MarkdownViewer content={skill.content} className="flex-1 flex flex-col" />
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-[280px] shrink-0">
          <div className="lg:sticky lg:top-6 space-y-4">
            {/* Info card */}
            <div className="rounded-lg border border-border/50 bg-card p-4 space-y-4">
              <div>
                <h1 className="text-lg font-semibold tracking-tight">{skill.name}</h1>
                <p className="text-sm text-muted-foreground mt-1">by {skill.author}</p>
              </div>

              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                <span className="px-2 py-0.5 rounded bg-muted">{skill.category}</span>
                {skill.installCountExternal > 0 && (
                  <span className="flex items-center gap-1">
                    <Download className="h-3 w-3" />
                    {formatCount(skill.installCountExternal)}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-2 pt-2 border-t border-border/50">
                <button
                  className={cn(
                    "w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors",
                    isInstalled
                      ? "border border-border text-foreground hover:bg-accent"
                      : "bg-foreground/90 text-background hover:bg-foreground/80",
                  )}
                  disabled={isInstallLoading}
                  onClick={isInstalled ? handleUninstall : handleInstall}
                >
                  {isInstallLoading ? (
                    "Loading..."
                  ) : isInstalled ? (
                    <>
                      <Check className="h-4 w-4" />
                      Installed
                    </>
                  ) : (
                    "Install"
                  )}
                </button>
                {skill.sourceUrl && (
                  <a
                    href={skill.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View Source
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
